/**
 * Transaction History Page - Combined DEX & CEX transaction history
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../utils/api';
import { cn } from '../utils/cn';
import { useWallet } from '../contexts/WalletContext';
import {
  ArrowUpRight,
  ArrowDownLeft,
  ArrowLeftRight,
  Clock,
  CheckCircle,
  XCircle,
  ExternalLink,
  RefreshCw,
  Filter,
  Download,
  Search,
  ChevronDown,
  Calendar,
} from 'lucide-react';

interface Transaction {
  id: string;
  type: 'swap' | 'deposit' | 'withdrawal' | 'transfer';
  venue: 'dex' | 'cex' | 'internal';
  chainId?: number;
  pair?: string;
  side?: 'buy' | 'sell';
  tokenIn?: string;
  tokenOut?: string;
  amountIn?: number;
  amountOut?: number;
  price?: number;
  fee?: number;
  feeToken?: string;
  txHash?: string;
  status: 'pending' | 'completed' | 'failed';
  dex?: string;
  cex?: string;
  orderId?: string;
  createdAt: string;
}

const CHAIN_INFO: Record<number, { name: string; explorer: string; icon: string }> = {
  1: { name: 'Ethereum', explorer: 'https://etherscan.io', icon: '⟠' },
  42161: { name: 'Arbitrum', explorer: 'https://arbiscan.io', icon: '🔷' },
  137: { name: 'Polygon', explorer: 'https://polygonscan.com', icon: '🟣' },
  56: { name: 'BSC', explorer: 'https://bscscan.com', icon: '🟡' },
  8453: { name: 'Base', explorer: 'https://basescan.org', icon: '🔵' },
  10: { name: 'Optimism', explorer: 'https://optimistic.etherscan.io', icon: '🔴' },
};

const TYPE_ICONS = {
  swap: ArrowLeftRight,
  deposit: ArrowDownLeft,
  withdrawal: ArrowUpRight,
  transfer: ArrowLeftRight,
};

const TYPE_COLORS = {
  swap: 'text-[#6366f1]',
  deposit: 'text-[#10b981]',
  withdrawal: 'text-[#ef4444]',
  transfer: 'text-[#f0b90b]',
};

export default function History() {
  const { wallet: connectedWallet, tradingMode } = useWallet();
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [venueFilter, setVenueFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d');

  // Fetch transaction history
  const { data: transactions = [], isLoading, refetch } = useQuery<Transaction[]>({
    queryKey: ['transaction-history', typeFilter, venueFilter, statusFilter, dateRange],
    queryFn: async () => {
      try {
        const params = new URLSearchParams();
        if (typeFilter !== 'all') params.append('type', typeFilter);
        if (venueFilter !== 'all') params.append('venue', venueFilter);
        if (statusFilter !== 'all') params.append('status', statusFilter);
        params.append('days', dateRange === 'all' ? '365' : dateRange.replace('d', ''));
        
        const response = await api.get(`/transaction-history?${params.toString()}`);
        return response.data;
      } catch {
        // Mock data for development
        return [
          {
            id: '1',
            type: 'swap',
            venue: 'dex',
            chainId: 1,
            pair: 'ETH-USDT',
            side: 'buy',
            tokenIn: 'USDT',
            tokenOut: 'ETH',
            amountIn: 1000,
            amountOut: 0.294,
            price: 3401.36,
            fee: 4.5,
            feeToken: 'ETH',
            txHash: '0x1234567890abcdef1234567890abcdef12345678',
            status: 'completed',
            dex: '1inch',
            createdAt: new Date(Date.now() - 3600000).toISOString(),
          },
          {
            id: '2',
            type: 'deposit',
            venue: 'cex',
            chainId: 1,
            tokenIn: 'ETH',
            amountIn: 0.5,
            status: 'completed',
            txHash: '0xabcdef1234567890abcdef1234567890abcdef12',
            createdAt: new Date(Date.now() - 7200000).toISOString(),
          },
          {
            id: '3',
            type: 'swap',
            venue: 'cex',
            pair: 'BTC-USDT',
            side: 'sell',
            tokenIn: 'BTC',
            tokenOut: 'USDT',
            amountIn: 0.01,
            amountOut: 985,
            price: 98500,
            fee: 0.985,
            feeToken: 'USDT',
            status: 'completed',
            cex: 'Binance',
            orderId: 'BIN123456',
            createdAt: new Date(Date.now() - 86400000).toISOString(),
          },
          {
            id: '4',
            type: 'withdrawal',
            venue: 'cex',
            chainId: 1,
            tokenOut: 'USDT',
            amountOut: 500,
            fee: 5,
            feeToken: 'USDT',
            status: 'pending',
            txHash: null,
            createdAt: new Date(Date.now() - 1800000).toISOString(),
          },
          {
            id: '5',
            type: 'swap',
            venue: 'dex',
            chainId: 42161,
            pair: 'ARB-USDT',
            side: 'buy',
            tokenIn: 'USDT',
            tokenOut: 'ARB',
            amountIn: 200,
            amountOut: 150.5,
            price: 1.33,
            fee: 0.8,
            feeToken: 'ETH',
            txHash: '0x9876543210fedcba9876543210fedcba98765432',
            status: 'completed',
            dex: 'Uniswap V3',
            createdAt: new Date(Date.now() - 172800000).toISOString(),
          },
        ] as Transaction[];
      }
    },
    refetchInterval: 30000,
  });

  // Filter transactions based on search
  const filteredTransactions = transactions.filter(tx => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      tx.pair?.toLowerCase().includes(query) ||
      tx.tokenIn?.toLowerCase().includes(query) ||
      tx.tokenOut?.toLowerCase().includes(query) ||
      tx.txHash?.toLowerCase().includes(query) ||
      tx.orderId?.toLowerCase().includes(query)
    );
  });

  // Format date
  const formatDate = (date: string) => {
    const d = new Date(date);
    return d.toLocaleDateString(undefined, { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Format time ago
  const timeAgo = (date: string) => {
    const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  // Get status badge
  const StatusBadge = ({ status }: { status: string }) => {
    const config = {
      completed: { icon: CheckCircle, text: 'Completed', className: 'text-[#10b981] bg-[#10b981]/10' },
      pending: { icon: Clock, text: 'Pending', className: 'text-[#f0b90b] bg-[#f0b90b]/10' },
      failed: { icon: XCircle, text: 'Failed', className: 'text-[#ef4444] bg-[#ef4444]/10' },
    }[status] || { icon: Clock, text: status, className: 'text-[#8b949e] bg-[#8b949e]/10' };
    
    const Icon = config.icon;
    return (
      <span className={cn("inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium", config.className)}>
        <Icon className="h-3 w-3" />
        {config.text}
      </span>
    );
  };

  // Export to CSV
  const exportCSV = () => {
    const headers = ['Date', 'Type', 'Venue', 'Pair', 'Side', 'Amount In', 'Amount Out', 'Price', 'Fee', 'Status', 'TX Hash'];
    const rows = filteredTransactions.map(tx => [
      new Date(tx.createdAt).toISOString(),
      tx.type,
      tx.venue,
      tx.pair || '-',
      tx.side || '-',
      tx.amountIn || '-',
      tx.amountOut || '-',
      tx.price || '-',
      tx.fee || '-',
      tx.status,
      tx.txHash || tx.orderId || '-',
    ]);
    
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `hybridx-history-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1">Transaction History</h1>
          <p className="text-[#848e9c]">View all your trading activity across DEX and CEX</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={exportCSV}
            className="btn-secondary btn-md"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
          <button
            onClick={() => refetch()}
            disabled={isLoading}
            className="btn-secondary btn-md"
          >
            <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            {/* Search */}
            <div className="relative flex-1 min-w-[240px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8b949e]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by pair, token, or TX hash..."
                className="input pl-10 text-sm"
              />
            </div>

            {/* Date Range */}
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as any)}
              className="select text-sm"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="all">All time</option>
            </select>

            {/* Type Filter */}
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="select text-sm"
            >
              <option value="all">All Types</option>
              <option value="swap">Swaps</option>
              <option value="deposit">Deposits</option>
              <option value="withdrawal">Withdrawals</option>
            </select>

            {/* Venue Filter */}
            <select
              value={venueFilter}
              onChange={(e) => setVenueFilter(e.target.value)}
              className="select text-sm"
            >
              <option value="all">All Venues</option>
              <option value="dex">DEX Only</option>
              <option value="cex">CEX Only</option>
            </select>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="select text-sm"
            >
              <option value="all">All Status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <RefreshCw className="h-8 w-8 animate-spin text-[#00d4aa] mx-auto mb-4" />
            <p className="text-[#848e9c]">Loading transactions...</p>
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="p-12 text-center">
            <Clock className="h-12 w-12 text-[#848e9c] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">No transactions found</h3>
            <p className="text-[#848e9c]">
              {searchQuery ? 'Try adjusting your search or filters' : 'Your transaction history will appear here'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.08]">
                  <th className="text-left px-6 py-4 text-xs font-semibold text-[#8b949e] uppercase tracking-wider">Type</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-[#8b949e] uppercase tracking-wider">Asset</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-[#8b949e] uppercase tracking-wider">Amount</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-[#8b949e] uppercase tracking-wider">Price</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-[#8b949e] uppercase tracking-wider">Venue</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-[#8b949e] uppercase tracking-wider">Status</th>
                  <th className="text-left px-6 py-4 text-xs font-semibold text-[#8b949e] uppercase tracking-wider">Date</th>
                  <th className="text-right px-6 py-4 text-xs font-semibold text-[#8b949e] uppercase tracking-wider">TX</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.06]">
                {filteredTransactions.map((tx) => {
                  const TypeIcon = TYPE_ICONS[tx.type];
                  const chain = tx.chainId ? CHAIN_INFO[tx.chainId] : null;
                  
                  return (
                    <tr key={tx.id} className="hover:bg-white/[0.02] transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "w-8 h-8 rounded-lg flex items-center justify-center",
                            TYPE_COLORS[tx.type].replace('text-', 'bg-') + '/20'
                          )}>
                            <TypeIcon className={cn("h-4 w-4", TYPE_COLORS[tx.type])} />
                          </div>
                          <div>
                            <span className="font-medium text-white capitalize">{tx.type}</span>
                            {tx.side && (
                              <span className={cn(
                                "ml-2 text-xs font-medium",
                                tx.side === 'buy' ? "text-[#10b981]" : "text-[#ef4444]"
                              )}>
                                {tx.side.toUpperCase()}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <span className="font-medium text-white">{tx.pair || tx.tokenIn || tx.tokenOut}</span>
                          {chain && (
                            <span className="ml-2 text-xs text-[#8b949e]">
                              {chain.icon} {chain.name}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        {tx.type === 'swap' ? (
                          <div className="text-sm">
                            <div className="text-[#ef4444]">-{tx.amountIn?.toLocaleString()} {tx.tokenIn}</div>
                            <div className="text-[#10b981]">+{tx.amountOut?.toLocaleString()} {tx.tokenOut}</div>
                          </div>
                        ) : tx.type === 'deposit' ? (
                          <div className="text-[#10b981] font-medium">+{tx.amountIn?.toLocaleString()} {tx.tokenIn}</div>
                        ) : (
                          <div className="text-[#ef4444] font-medium">-{tx.amountOut?.toLocaleString()} {tx.tokenOut}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-white font-mono">
                        {tx.price ? `$${tx.price.toLocaleString()}` : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={cn(
                          "px-2 py-1 rounded text-xs font-medium",
                          tx.venue === 'dex' 
                            ? "bg-[#6366f1]/20 text-[#6366f1]"
                            : "bg-[#f0b90b]/20 text-[#f0b90b]"
                        )}>
                          {tx.venue.toUpperCase()}
                        </span>
                        {(tx.dex || tx.cex) && (
                          <span className="ml-2 text-xs text-[#8b949e]">
                            {tx.dex || tx.cex}
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge status={tx.status} />
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm">
                          <div className="text-white">{formatDate(tx.createdAt)}</div>
                          <div className="text-xs text-[#8b949e]">{timeAgo(tx.createdAt)}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        {tx.txHash ? (
                          <a
                            href={`${chain?.explorer || 'https://etherscan.io'}/tx/${tx.txHash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-[#00d4aa] hover:underline text-sm"
                          >
                            {tx.txHash.slice(0, 8)}...
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : tx.orderId ? (
                          <span className="text-[#8b949e] text-sm font-mono">{tx.orderId}</span>
                        ) : (
                          <span className="text-[#8b949e] text-sm">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      {filteredTransactions.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <div className="card p-4">
            <p className="text-[#8b949e] text-sm mb-1">Total Transactions</p>
            <p className="text-2xl font-bold text-white">{filteredTransactions.length}</p>
          </div>
          <div className="card p-4">
            <p className="text-[#8b949e] text-sm mb-1">DEX Volume</p>
            <p className="text-2xl font-bold text-white">
              ${filteredTransactions
                .filter(tx => tx.venue === 'dex' && tx.amountIn)
                .reduce((sum, tx) => sum + (tx.amountIn || 0), 0)
                .toLocaleString()}
            </p>
          </div>
          <div className="card p-4">
            <p className="text-[#8b949e] text-sm mb-1">CEX Volume</p>
            <p className="text-2xl font-bold text-white">
              ${filteredTransactions
                .filter(tx => tx.venue === 'cex' && tx.amountIn)
                .reduce((sum, tx) => sum + (tx.amountIn || 0), 0)
                .toLocaleString()}
            </p>
          </div>
          <div className="card p-4">
            <p className="text-[#8b949e] text-sm mb-1">Total Fees Paid</p>
            <p className="text-2xl font-bold text-white">
              ${filteredTransactions
                .filter(tx => tx.fee)
                .reduce((sum, tx) => sum + (tx.fee || 0), 0)
                .toLocaleString()}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
